import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { Icon } from '@/design-system/assets/icons/Icon.jsx';
import type { IconName } from '@/design-system/assets/icons/Icon';
import { useTheme } from '../../contexts/ThemeContext';

interface MobileNavSection {
  id: string;
  title: string;
  items: Array<{
    id: string;
    label: string;
    icon: React.ReactNode;
    href: string;
    badge?: string | number;
  }>;
}

interface MobileBottomNavProps {
  sections: MobileNavSection[];
  onLogout?: () => void;
}

const HOME_TAB = { id: 'home', label: '홈', hrefs: ['/', '/dashboard'] };

// 홈은 중앙의 로고 버튼으로 떠오르므로 좌우 2개씩 배치되는 나머지 탭만 여기 둔다.
const PRIMARY_TABS: Array<{ id: string; label: string; icon: IconName; hrefs: string[] }> = [
  { id: 'purchase-history', label: '구매내역', icon: 'CreditCard', hrefs: ['/purchase-history'] },
  { id: 'vehicles', label: '차량관리', icon: 'DirectionsCar', hrefs: ['/vehicles'] },
  { id: 'work-schedule', label: '근무편성', icon: 'Schedule2', hrefs: ['/work-schedule'] },
];

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ sections, onLogout }) => {
  const { theme, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [canScrollMore, setCanScrollMore] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isMenuOpen) return;
    const el = scrollRef.current;
    if (!el) return;
    const checkScroll = () => setCanScrollMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [isMenuOpen, sections]);

  const isPrimaryActive = (hrefs: string[]) => hrefs.includes(location.pathname);
  const isHomeActive = isPrimaryActive(HOME_TAB.hrefs);
  const anyPrimaryActive = isHomeActive || PRIMARY_TABS.some(tab => isPrimaryActive(tab.hrefs));
  const allMenuActive = isMenuOpen || !anyPrimaryActive;

  const handleNavigate = (href: string) => {
    setIsMenuOpen(false);
    navigate(href);
  };

  const handleLogoutClick = () => {
    setIsMenuOpen(false);
    onLogout?.();
  };

  return (
    <>
      {/* 전체메뉴 바텀시트 */}
      <div
        className={clsx(
          'fixed inset-0 z-[60] lg:hidden print:hidden',
          !isMenuOpen && 'pointer-events-none'
        )}
        aria-hidden={!isMenuOpen}
      >
        <div
          className={clsx(
            'absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300',
            isMenuOpen ? 'opacity-100' : 'opacity-0'
          )}
          onClick={() => setIsMenuOpen(false)}
        />
        <div
          className={clsx(
            'absolute bottom-0 left-0 right-0 rounded-t-3xl shadow-2xl overflow-hidden',
            'transition-transform duration-300 ease-out',
            isMenuOpen ? 'translate-y-0' : 'translate-y-full'
          )}
          role="dialog"
          aria-modal="true"
          aria-label="전체 메뉴"
        >
          <div
            ref={scrollRef}
            className={clsx(
              'bg-card/75 backdrop-blur-2xl border-t border-white/25 dark:border-white/10',
              'max-h-[75vh] overflow-y-auto overscroll-contain',
              'pb-[calc(env(safe-area-inset-bottom)+5rem)] pt-2'
            )}
          >
            <div className="w-10 h-1.5 bg-border rounded-full mx-auto mt-2 mb-4" />
            <div className="px-5 flex flex-col gap-6">
              {sections.map(section => (
                <div key={section.id}>
                  <h3 className="text-xs font-bold text-muted-foreground mb-3">{section.title}</h3>
                  <div className="grid grid-cols-4 gap-y-4">
                    {section.items.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleNavigate(item.href)}
                        className="flex flex-col items-center gap-1.5 group"
                      >
                        <span className="relative w-12 h-12 rounded-2xl bg-muted/60 group-active:bg-primary/15 flex items-center justify-center text-muted-foreground group-active:text-primary transition-colors">
                          {item.icon}
                          {item.badge && (
                            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                              {item.badge}
                            </span>
                          )}
                        </span>
                        <span className="text-[11px] font-medium text-foreground text-center leading-tight">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="pt-2 border-t border-border/50 flex flex-col gap-1">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="w-full flex items-center gap-3 px-1 py-3 text-foreground active:bg-muted/60 rounded-xl transition-colors"
                >
                  <span className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center text-muted-foreground">
                    <Icon name={theme === 'light' ? 'DarkMode' : 'LightMode'} size={18} />
                  </span>
                  <span className="text-sm font-semibold">{theme === 'light' ? '다크 모드' : '라이트 모드'}</span>
                </button>

                {onLogout && (
                  <button
                    type="button"
                    onClick={handleLogoutClick}
                    className="w-full flex items-center gap-3 px-1 py-3 text-destructive active:bg-destructive/10 rounded-xl transition-colors"
                  >
                    <span className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                      <Icon name="LogOut" size={18} />
                    </span>
                    <span className="text-sm font-semibold">로그아웃</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 스크롤 힌트: 아래 내용이 더 있을 때만 표시 */}
          <div
            className={clsx(
              'pointer-events-none absolute inset-x-0 bottom-0 h-14',
              'bg-gradient-to-t from-card/95 to-transparent',
              'transition-opacity duration-200',
              canScrollMore ? 'opacity-100' : 'opacity-0'
            )}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* 하단 탭바: 화면 하단에 고정, 위쪽 모서리만 라운드 + 중앙 로고 버튼 */}
      <nav
        className="fixed inset-x-0 bottom-0 z-50 lg:hidden print:hidden"
        aria-label="주요 메뉴"
      >
        <div className="relative rounded-t-3xl bg-card/85 backdrop-blur-2xl border-t border-white/25 dark:border-white/10 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] pb-[env(safe-area-inset-bottom)]">
          <div className="relative grid grid-cols-5 h-16">
            {/* 좌측 2개: 구매, 차량 */}
            {PRIMARY_TABS.slice(0, 2).map(tab => (
              <NavTabButton
                key={tab.id}
                tab={tab}
                active={isPrimaryActive(tab.hrefs)}
                onClick={() => handleNavigate(tab.hrefs[0])}
              />
            ))}

            {/* 중앙: 홈 - 대시보드 웰컴 카드의 보금자리 하우스 일러스트를 배지 아이콘으로 재사용 */}
            <div className="relative h-full">
              <button
                type="button"
                onClick={() => handleNavigate(HOME_TAB.hrefs[0])}
                title={HOME_TAB.label}
                aria-label={HOME_TAB.label}
                className={clsx(
                  'absolute left-1/2 -top-6 -translate-x-1/2 z-10',
                  'w-16 h-16 rounded-full bg-primary',
                  'flex items-center justify-center',
                  'ring-4 shadow-[0_10px_28px_rgba(79,70,229,0.45)]',
                  'transition-all duration-200 ease-out active:scale-90',
                  isHomeActive ? 'ring-primary-foreground/80' : 'ring-background'
                )}
              >
                <svg viewBox="26 0 148 182" aria-hidden="true" className="w-9 h-9">
                  <path
                    d="M100 22 L168 76 L168 168 Q168 176 160 176 L40 176 Q32 176 32 168 L32 76 Z"
                    className="fill-primary-foreground"
                  />
                  <rect x="84" y="122" width="32" height="54" rx="8" className="fill-primary" />
                  <path
                    d="M150 38c0-6.5-5.2-11-11-11-4 0-7.3 2-9 5-1.7-3-5-5-9-5-5.8 0-11 4.5-11 11 0 9 10 15.5 20 24 10-8.5 20-15 20-24z"
                    className="fill-primary-foreground"
                  />
                  {/* 촛불 기둥 (1004 로고의 초 모티프) */}
                  <rect x="52" y="20" width="14" height="40" rx="6" className="fill-primary-foreground" />
                  <path
                    d="M59 20 C 63 14, 55 10, 59 4"
                    className="stroke-primary-foreground"
                    fill="none"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* 우측 2개: 근무, 전체메뉴 */}
            <NavTabButton
              tab={PRIMARY_TABS[2]}
              active={isPrimaryActive(PRIMARY_TABS[2].hrefs)}
              onClick={() => handleNavigate(PRIMARY_TABS[2].hrefs[0])}
            />
            <NavTabButton
              tab={{ id: 'menu', label: '전체메뉴', icon: isMenuOpen ? 'Close' : 'Menu' }}
              active={allMenuActive}
              onClick={() => setIsMenuOpen(prev => !prev)}
              expanded={isMenuOpen}
            />
          </div>
        </div>
      </nav>
    </>
  );
};

const NavTabButton: React.FC<{
  tab: { id: string; label: string; icon: IconName };
  active: boolean;
  onClick: () => void;
  expanded?: boolean;
}> = ({ tab, active, onClick, expanded }) => (
  <button
    type="button"
    onClick={onClick}
    title={tab.label}
    aria-label={tab.label}
    aria-expanded={expanded}
    className="relative z-10 flex flex-col items-center justify-center h-full gap-1 active:scale-95 transition-transform"
  >
    <Icon name={tab.icon} size={22} className={clsx('transition-colors', active ? 'text-primary' : 'text-muted-foreground')} />
    <span className={clsx('text-[10px] leading-none transition-colors', active ? 'text-primary font-semibold' : 'text-muted-foreground font-medium')}>
      {tab.label}
    </span>
  </button>
);
